'use client';

import { InnBusinessServerSideProps } from '@whitbread-eos/api';
import { Toaster, Analytics } from '@whitbread-eos/atoms/ui';
import {
  Header,
  Sidebar,
  SidebarMobile,
  Main,
  BusinessStepType,
  Footer,
  EditSearch,
} from '@whitbread-eos/layout';
import { FeatureToggleContextProvider } from '@whitbread-eos/utils';
import { formatIBAssetsUrl, TranslationProvider, cn } from '@whitbread-eos/utils/server';
import { NavigationGuardProvider } from 'next-navigation-guard';
import { useState } from 'react';

import {
  BusinessBookerPageContent,
  BusinessBookerPageContentProps,
} from './BusinessBookerPageContent';

interface Props {
  children: React.ReactNode;
  serverSideProps: InnBusinessServerSideProps;
  businessStepType?: BusinessStepType;
  showSidebar?: boolean;
  showFooter?: boolean;
  showEditSearch?: boolean;
  collapsedSidebar?: boolean;
  isBusinessBookerPage?: boolean;
  businessBookerPageOptions?: BusinessBookerPageContentProps;
  headerLogoOnly?: boolean;
  featureToggle?: Record<string, boolean>;
  secureUrl?: string;
}

export default function InnBusinessLayout({
  children,
  serverSideProps,
  businessStepType,
  showSidebar = true,
  showFooter = false,
  showEditSearch = false,
  collapsedSidebar = false,
  isBusinessBookerPage = false,
  businessBookerPageOptions,
  headerLogoOnly = false,
  featureToggle = {},
  secureUrl = '',
}: Readonly<Props>) {
  const {
    labels,
    icons,
    cards,
    userDetails,
    companyDetails,
    footer,
    language,
    searchRules,
    layout,
  } = serverSideProps;
  const translations = { common: labels, cards, footer, layout };

  const [isEditSearchVisible, setIsEditSearchVisible] = useState(showEditSearch);

  const hideEditSearch = () => {
    setIsEditSearchVisible(false);
  };

  return (
    <>
      <TranslationProvider value={translations}>
        <FeatureToggleContextProvider defaultFeatureToggles={featureToggle}>
          <NavigationGuardProvider>
            <Header
              logoUrl={formatIBAssetsUrl(labels.content.header.image)}
              companyLabel={companyDetails?.requestedCompany?.companyDetails?.companyName}
              accountName={{
                firstName: userDetails.contactDetail.firstName,
                lastName: userDetails.contactDetail.lastName,
              }}
              languages={labels.content.countries}
              userRole={userDetails.business.accessLevel}
              searchFormLabels={labels.content.form}
              icons={icons}
              globalLabels={labels.content.global}
              businessStepType={businessStepType}
              hideEditSearch={hideEditSearch}
              isEditSearchVisible={isEditSearchVisible}
              logoOnly={headerLogoOnly}
              userDetails={userDetails}
              searchRules={searchRules}
              secureUrl={secureUrl}
            />

            {showSidebar && (
              <Sidebar
                userRole={userDetails.business.accessLevel}
                isAccountHolder={serverSideProps.isAccountHolder}
                isCardHolder={serverSideProps.isCardHolder}
                menuLabels={labels.layout.menu || {}}
                icons={icons}
                collapsed={collapsedSidebar}
              />
            )}

            <Main
              hasSidebar={showSidebar}
              collapsedSidebar={collapsedSidebar}
              className={cn(
                isBusinessBookerPage && businessBookerPageStyle,
                isBusinessBookerPage &&
                  businessBookerPageOptions?.isHotelDetailsPage &&
                  hotelDetailsPageStyle
              )}
            >
              {isEditSearchVisible && (
                <EditSearch
                  searchIcon={formatIBAssetsUrl(labels.content.form.searchIcon)}
                  handleEditClick={hideEditSearch}
                  language={language ?? 'en'}
                />
              )}
              {children}
              {showFooter && <Footer />}
            </Main>

            {showSidebar && (
              <SidebarMobile
                userRole={userDetails.business.accessLevel}
                isAccountHolder={serverSideProps.isAccountHolder}
                menuLabels={labels.layout.menu || {}}
                icons={icons}
              />
            )}
          </NavigationGuardProvider>
        </FeatureToggleContextProvider>
      </TranslationProvider>

      {isBusinessBookerPage && <BusinessBookerPageContent {...businessBookerPageOptions} />}

      <Toaster />
      {!isBusinessBookerPage && <Analytics userDetails={userDetails} />}
    </>
  );
}

const businessBookerPageStyle = 'items-center';
const hotelDetailsPageStyle = 'pt-2 mobile:pt-0';
